import { computed, defineAsyncComponent } from 'vue';
import { themeRegistry } from '@/themes/registry';
import type { BlockType, ThemeHost } from '@/themes/types';

/**
 * The blocks a layout draws, and the component of each. A block whose type has no component in the theme or its
 * parent is left out, so the others still render; in development the registry throws instead, so the gap is seen
 * where it is introduced.
 */
export function useDrawnBlocks<T>(
  host: ThemeHost,
  themeKey: () => string,
  blocks: () => T[],
  typeOf: (block: T) => BlockType,
) {
  const drawnBlocks = computed(() =>
    blocks().filter((block) => themeRegistry.hasBlockComponent(host, themeKey(), typeOf(block))),
  );

  // Only ever called for a drawn block, which is what makes the non-null assertion hold.
  const componentOf = (block: T) =>
    defineAsyncComponent(themeRegistry.getBlockComponent(host, themeKey(), typeOf(block))!);

  return { drawnBlocks, componentOf };
}
